import { DefaultTheme, getPathFromState, NavigationContainer } from "@react-navigation/native"
import React, { useContext, useEffect, useMemo } from "react"
import FullScreenLoading from "../utility/full-screen-loading"
import { createStackNavigator } from "@react-navigation/stack"
import { log } from "../utility"
import { linking } from "./linking-configuration"
import { useAppDispatch, useAppSelector } from '../store'
import { logOut } from "../auth-slice"
import api, { initApi } from "../api-client"
import { clearAccounts, fetchAccounts } from "../accounts-slice"
import { clearValues, fetchValues } from "../values-slice"
import { fetchDatasources } from "../datasources-slice"
import { clearFeaturedTags, fetchFeaturedTags } from "../featured-tags-slice"
import Login from "../auth/login"
import { clearCustomerImpact } from "../customer-impact-slice"
import * as Sentry from "../utility/sentry"
import { useAuthContext, useTracking } from "../utility/context"
import AuthenticatedStack from "./nav-stacks/AuthenticatedStack"
import MfaStack from "./nav-stacks/MfaStack"
import DeleteAccountStack from "./nav-stacks/DeleteAccountStack"
import { navigationRef } from "../root-navigation"
import { ThemeContext } from "../utility/context/ThemeContext"

const Stack = createStackNavigator()

const NavigationContainerWrapper = (): JSX.Element => {
  const { onNavigationReady, onNavigationStateChange } = useTracking()
  const { theme } = useContext(ThemeContext)
  const navigationTheme = useMemo(() => ({
    ...DefaultTheme,
    colors: {
      ...DefaultTheme.colors,
      background: theme.colors.bgMain,
    },
  }), [theme.colors])

  return (
    <NavigationContainer
      ref={navigationRef}
      theme={navigationTheme}
      linking={linking}
      onUnhandledAction={(action) => {
        log.error(new Error(`Unhandled action: ${action}`), { tags: { message: 'unhandled action' } })
      }}
      onReady={async () => {
        // delegate tracking init to context
        onNavigationReady(navigationRef, linking)
      }}
      // we can add tags to know some state details
      onStateChange={async (state: Parameters<typeof getPathFromState>[0] | undefined) => {
        // delegate tracking to context
        onNavigationStateChange(state, navigationRef, linking)

        // SENTRY: define screen and params tags
        if (!state) {
          Sentry.setTag('screen', undefined)
          Sentry.setTag('params', undefined)
          return
        }

        try {
          const splitPath = getPathFromState(
            state,
            linking.config as Parameters<typeof getPathFromState>[1]
          ).split('?')
          const screen = splitPath[0]
          const params = splitPath.length > 1 ? splitPath[1] : undefined
          Sentry.setTag('screen', screen)
          Sentry.setTag('params', params)
        }
        catch (error) {
          Sentry.setTag('screen', undefined)
          Sentry.setTag('params', undefined)
          log.error(error, { tags: { message: 'error setting navigation tags' } })
        }
      }}
    >
      <AppNavigation />
    </NavigationContainer>
  )
}

const AppNavigation = (): JSX.Element => {
  const {
    isLoading: authLoading,
    user: authedUser,
    isAuthenticated,
    logout,
    getAccessToken
  } = useAuthContext()
  const { status: userStatus, user } = useAppSelector((state) => state.auth)
  const userLoading = userStatus === 'loading'
  const userDefined = user && Object.keys(user).length > 0

  const dispatch = useAppDispatch()

  // on load, we should initialize the api client
  useEffect(() => {
    initApi({
      onTokenError: async () => {
        await api.clearToken()
        await logout()
      },
      // onTokenUpdate: (token) => {
      //   return null
      // },
      getAccessToken
    })
  }, [getAccessToken, logout])

  // on user state change, we should handle the auth state present
  // if user is authenticated, we should get the user redux values for experience
  // if user is not authenticated, we should redirect to login, clearing redux state
  useEffect(() => {
    if (authLoading) return

    const handleAuthState = async () => {
      const isAuthed = await isAuthenticated()
      if (isAuthed && authedUser) {
        dispatch(fetchAccounts())
        dispatch(fetchValues())
        dispatch(fetchDatasources())
        dispatch(fetchFeaturedTags())
      } else {
        dispatch(logOut())
        dispatch(clearAccounts())
        dispatch(clearCustomerImpact())
        dispatch(clearFeaturedTags())
        dispatch(clearValues())
      }
    }

    handleAuthState()

  }, [authLoading, authedUser, dispatch, isAuthenticated])

  if (authLoading || (!!authedUser && userLoading)) {
    return <FullScreenLoading loading/>
  }

  return (
    <Stack.Navigator screenOptions={{headerShown: false, gestureEnabled: false}} id={undefined}>
      {
        !authedUser || !userDefined ? <>
        <Stack.Screen
            name="Login"
            component={Login}
          />
        </> : <>
          <Stack.Screen
            name="AuthRoutes"
            component={AuthenticatedStack}
          />
        </>
      }
      <Stack.Screen
        name="Mfa"
        component={MfaStack}
      />
      <Stack.Screen
        name="DeleteAccount"
        component={DeleteAccountStack}
        options={{ gestureEnabled: false }}
      />
    </Stack.Navigator>
  )
}
export default NavigationContainerWrapper